import { useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import {
  RefreshCw, Download, Upload, Trash2, AlertTriangle,
  Database, RotateCcw, X, FolderArchive
} from "lucide-react";

interface BackupItem {
  filename: string;
  createdAt: string | null;
  walletCount: number;
  totalBalance: string;
  restoredAt: string | null;
  sizeBytes: number;
}

const WalletBackup: React.FC = () => {
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [restoringFile, setRestoringFile] = useState<string | null>(null);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);

  // Modals
  const [showResetModal, setShowResetModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");

  // Status message
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMsg({ type, text });
    setTimeout(() => setStatusMsg(null), 5000);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const fetchBackups = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/api/wallet-backup/list");
      if (response.data.status === "success") {
        setBackups(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching backups:", error);
      showStatus("error", "Failed to fetch backup list");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const handleCreateBackup = async () => {
    try {
      setIsCreating(true);
      const response = await api.post("/api/wallet-backup/create");
      if (response.data.status === "success") {
        const { walletCount, totalBalance } = response.data.data;
        showStatus("success", `Backup created successfully! ${walletCount} wallets, total balance: $${parseFloat(totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
        fetchBackups();
      }
    } catch (error: any) {
      console.error("Error creating backup:", error);
      showStatus("error", error.response?.data?.message || "Failed to create backup");
    } finally {
      setIsCreating(false);
    }
  };

  const handleResetWallets = async () => {
    if (confirmText !== "RESET_ALL_WALLETS") return;
    try {
      setIsResetting(true);
      const response = await api.post("/api/wallet-backup/reset", {
        confirm: "RESET_ALL_WALLETS",
      });
      if (response.data.status === "success") {
        const { affectedCount, previousTotalBalance, autoBackupFile } = response.data.data;
        showStatus("success", `All wallets reset to 0. ${affectedCount} wallets affected. Previous total: $${parseFloat(previousTotalBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}. Auto-backup saved: ${autoBackupFile}`);
        setShowResetModal(false);
        setConfirmText("");
        fetchBackups();
      }
    } catch (error: any) {
      console.error("Error resetting wallets:", error);
      showStatus("error", error.response?.data?.message || "Failed to reset wallets");
    } finally {
      setIsResetting(false);
    }
  };

  const handleRestore = async (filename: string) => {
    try {
      setRestoringFile(filename);
      const response = await api.post("/api/wallet-backup/restore", { filename });
      if (response.data.status === "success") {
        const { restoredCount, totalRestored } = response.data.data;
        showStatus("success", `Restored ${restoredCount} wallets. Total added back: $${parseFloat(totalRestored).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
        setShowRestoreModal(null);
      }
    } catch (error: any) {
      console.error("Error restoring backup:", error);
      showStatus("error", error.response?.data?.message || "Failed to restore from backup");
    } finally {
      setRestoringFile(null);
    }
  };

  const handleDelete = async (filename: string) => {
    if (!window.confirm(`Delete backup "${filename}"? This cannot be undone.`)) return;
    try {
      setDeletingFile(filename);
      await api.delete(`/api/wallet-backup/delete/${filename}`);
      showStatus("success", "Backup deleted successfully");
      fetchBackups();
    } catch (error: any) {
      console.error("Error deleting backup:", error);
      showStatus("error", error.response?.data?.message || "Failed to delete backup");
    } finally {
      setDeletingFile(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6 text-center md:text-left">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#523680] tracking-tight">Wallet Backup & Reset</h1>
          <p className="mt-1 text-sm md:text-base text-gray-500 font-medium">Backup, restore, or reset all user wallet balances</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchBackups()}
            className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm group"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 text-gray-400 group-hover:text-[#523680] ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Status Banner */}
      {statusMsg && (
        <div className={`p-4 rounded-2xl border flex items-center gap-3 ${statusMsg.type === "success"
          ? "bg-green-50 border-green-200 text-green-800"
          : "bg-red-50 border-red-200 text-red-800"
          }`}>
          <span className="text-sm font-medium flex-1">{statusMsg.text}</span>
          <button onClick={() => setStatusMsg(null)} className="p-1 hover:bg-black/5 rounded-lg">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Create Backup Card */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#523680]/10 flex items-center justify-center">
              <Database className="h-5 w-5 text-[#523680]" />
            </div>
            <h3 className="text-lg font-bold text-gray-800">Create Backup</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Creates a JSON snapshot of all current wallet balances. The backup can be used to restore balances later.
          </p>
          <button
            onClick={handleCreateBackup}
            disabled={isCreating}
            className="w-full py-3 bg-[#523680] text-white rounded-xl text-sm font-bold hover:bg-[#523680]/90 transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isCreating ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {isCreating ? "Creating Backup..." : "Create Wallet Backup"}
          </button>
        </div>

        {/* Reset All Wallets Card */}
        <div className="bg-red-50/50 rounded-2xl p-6 border border-red-200 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-red-800">Reset All Wallets</h3>
          </div>
          <p className="text-sm text-red-600/80 mb-4">
            Sets ALL user wallet balances to 0. An automatic backup is created before reset as a safety net.
          </p>
          <button
            onClick={() => setShowResetModal(true)}
            className="w-full py-3 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-all shadow-sm flex items-center justify-center gap-2"
          >
            <AlertTriangle className="h-4 w-4" />
            Reset All Wallet Balances
          </button>
        </div>
      </div>

      {/* Backup List */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="p-4 md:p-6 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FolderArchive className="h-5 w-5 text-[#523680]" />
            <h2 className="text-lg font-bold text-gray-800">Backup History</h2>
          </div>
          <span className="text-xs font-bold text-gray-400 uppercase">{backups.length} backup{backups.length !== 1 ? "s" : ""}</span>
        </div>

        {isLoading && backups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#523680] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : backups.length > 0 ? (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Wallets</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Total Balance</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Status</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Size</th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {backups.map((backup) => (
                    <tr key={backup.filename} className="hover:bg-gray-50/50 transition-all">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-700">
                          {backup.createdAt
                            ? new Date(backup.createdAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
                            : "Unknown"}
                        </p>
                        <p className="text-[10px] font-mono text-gray-400 mt-0.5">{backup.filename}</p>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-700 text-right">
                        {backup.walletCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-[#523680] text-right">
                        ${parseFloat(backup.totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {backup.restoredAt ? (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 border border-green-200" title={`Restored on ${new Date(backup.restoredAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}`}>
                            Restored
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-200">
                            Available
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-right font-medium">
                        {formatBytes(backup.sizeBytes)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setShowRestoreModal(backup.filename)}
                            disabled={!!backup.restoredAt || restoringFile === backup.filename}
                            className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-bold hover:bg-green-100 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {restoringFile === backup.filename ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <Upload className="h-3 w-3" />
                            )}
                            {backup.restoredAt ? "Restored" : "Restore"}
                          </button>
                          <button
                            onClick={() => handleDelete(backup.filename)}
                            disabled={deletingFile === backup.filename}
                            className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-bold hover:bg-red-100 transition-all flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {deletingFile === backup.filename ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden p-4 space-y-3">
              {backups.map((backup) => (
                <div key={backup.filename} className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <div className="flex justify-between items-start mb-3">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      {backup.createdAt
                        ? new Date(backup.createdAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
                        : "Unknown"}
                    </div>
                    <span className="text-[10px] font-mono text-gray-400">{formatBytes(backup.sizeBytes)}</span>
                  </div>
                  <div className="flex justify-between items-end mb-3">
                    <div className="space-y-1">
                      <p className="text-[10px] text-gray-400 font-medium">Wallets: {backup.walletCount}</p>
                      <p className="text-lg font-black text-[#523680]">
                        ${parseFloat(backup.totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    {backup.restoredAt ? (
                      <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter bg-green-100 text-green-700">Restored</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter bg-blue-50 text-blue-600">Available</span>
                    )}
                  </div>
                  <div className="flex gap-2 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => setShowRestoreModal(backup.filename)}
                      disabled={!!backup.restoredAt || restoringFile === backup.filename}
                      className="flex-1 py-2 bg-green-50 text-green-700 border border-green-200 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Upload className="h-3 w-3" />
                      {backup.restoredAt ? "Restored" : "Restore"}
                    </button>
                    <button
                      onClick={() => handleDelete(backup.filename)}
                      disabled={deletingFile === backup.filename}
                      className="flex-1 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="py-20 flex flex-col items-center justify-center">
            <FolderArchive className="h-12 w-12 text-gray-200 mb-4" />
            <p className="text-gray-400 font-medium">No backups found</p>
            <p className="text-xs text-gray-300 mt-1">Create your first wallet backup above</p>
          </div>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Reset All Wallets</h2>
              </div>
              <button
                onClick={() => { setShowResetModal(false); setConfirmText(""); }}
                className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <p className="text-sm text-red-700 font-medium">
                  This will set ALL user wallet balances to <strong>0</strong>. An automatic backup will be created before the reset.
                </p>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-2">
                  Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">RESET_ALL_WALLETS</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESET_ALL_WALLETS"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-300 outline-none text-sm font-mono font-bold text-gray-800 placeholder-gray-300"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowResetModal(false); setConfirmText(""); }}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-bold hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetWallets}
                  disabled={confirmText !== "RESET_ALL_WALLETS" || isResetting}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isResetting ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  {isResetting ? "Resetting..." : "Reset All Wallets"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <RotateCcw className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Restore Backup</h2>
              </div>
              <button
                onClick={() => setShowRestoreModal(null)}
                className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-600 transition-all"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-700 font-medium">
                  This will ADD the backed-up balances to current wallet balances. For example, if a user had $500 at backup time and currently has $60, their balance will become $560. New users not in the backup will remain unchanged.
                </p>
              </div>

              <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Backup File</p>
                <p className="text-sm font-mono text-gray-700">{showRestoreModal}</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowRestoreModal(null)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-bold hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRestore(showRestoreModal)}
                  disabled={restoringFile === showRestoreModal}
                  className="flex-1 py-3 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {restoringFile === showRestoreModal ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {restoringFile === showRestoreModal ? "Restoring..." : "Restore Backup"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletBackup;
